import { supabase } from '../supabaseClient';

// Your Tally enquiry form. Trip details are appended as query params so the
// form arrives pre-filled. Add matching hidden fields in Tally (named
// trip, destination, days, link, email, note) to capture them there too.
export const TALLY_URL = 'https://tally.so/r/BzeAA1';

/**
 * Build a Tally URL pre-filled with the attached trip details.
 */
export function buildConciergeTallyUrl({ tripTitle, destination, tripLength, tripLink, contactEmail, note } = {}) {
  const params = new URLSearchParams();
  if (tripTitle) params.set('trip', tripTitle);
  if (destination) params.set('destination', destination);
  if (tripLength) params.set('days', String(tripLength));
  if (tripLink) params.set('link', tripLink);
  if (contactEmail) params.set('email', contactEmail);
  if (note) params.set('note', note);
  const qs = params.toString();
  return qs ? `${TALLY_URL}?${qs}` : TALLY_URL;
}

/**
 * Save a concierge hand-off request (with the built trip attached) to Supabase.
 * Anonymous visitors are allowed to insert (user_id stays null) — see the
 * 2026-10-16 RLS migration.
 */
export async function submitConciergeRequest({
  itineraryId = null,
  source = 'generic',
  tripTitle = null,
  destination = null,
  tripLength = null,
  tripLink = null,
  contactEmail = null,
  note = null,
} = {}) {
  const { data: { user } } = await supabase.auth.getUser();
  const { error } = await supabase.from('concierge_requests').insert({
    user_id: user?.id ?? null,
    itinerary_id: itineraryId,
    source,
    trip_title: tripTitle,
    destination,
    trip_length: tripLength,
    trip_link: tripLink,
    contact_email: contactEmail || user?.email || null,
    note,
  });
  if (error) throw error;
}

const apiUrl = () => process.env.REACT_APP_API_URL || 'https://travelatlas.onrender.com';

/**
 * Submit the in-house Atlas Plan brief form — replaces the external Tally
 * hand-off. Writes the full brief to Supabase (structured fields in
 * brief_details) and best-effort pings the backend to email Alix so a new
 * lead never sits unseen (see migrations/20261020_concierge_requests_brief_details.sql).
 */
export async function submitAtlasPlanBrief({
  fullName,
  contactEmail,
  destination,
  tripLengthBand,
  travelDates,
  groupSize,
  travelStyle = [],
  budget,
  pace,
  mustDo = '',
  avoidList = '',
  extraNotes = '',
} = {}) {
  const { data: { user } } = await supabase.auth.getUser();

  const brief_details = {
    full_name: fullName,
    travel_dates: travelDates,
    group_size: groupSize,
    travel_style: travelStyle,
    budget,
    pace,
    must_do: mustDo,
    avoid_list: avoidList,
    extra_notes: extraNotes,
    trip_length_band: tripLengthBand,
  };

  const { error } = await supabase.from('concierge_requests').insert({
    user_id: user?.id ?? null,
    source: 'atlas_plan_brief',
    destination,
    contact_email: contactEmail,
    note: [mustDo, avoidList, extraNotes].filter(Boolean).join(' | ') || null,
    brief_details,
  });
  if (error) throw error;

  // Best-effort — a failed email must never block the lead from being saved
  // (the write above already succeeded regardless of what happens here).
  fetch(`${apiUrl()}/api/leads/notify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fullName, contactEmail, destination, tripLengthBand, brief_details }),
  }).catch((err) => console.error('Lead notify ping failed:', err));
}
